import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import accountService from '../services/accountService';
import { updateProfile } from '../store/slices/authSlice';

/**
 * Custom hook for managing account settings
 * @returns {Object} Account settings state and handlers
 */
const useAccountSettings = () => {
  const { t, i18n } = useTranslation('hooks');
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // Profile data
    companyName: '',
    companyAddress: '',
    taxId: '',
    phoneNumber: '',
    email: '',
    // Shipping address
    shippingAddress: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingCountry: '', // This will now store country code (e.g., "CH")
    // Billing address
    billingAddress: '',
    billingCity: '',
    billingPostalCode: '',
    billingCountry: '', // This will now store country code (e.g., "CH")
    // Password change
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    // Notification preferences
    emailNotifications: true,
    orderUpdates: true,
    promotionalEmails: false,
    // Language
    language: 'en',
  });

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await accountService.getProfile();

        // Ensure country codes are properly formatted
        // If a country name is stored instead of a code, it will be handled by the UI
        const formattedData = {
          ...data,
          ...data.metadata,
          // Set default country code if missing (CH for Switzerland)
          shippingCountry: data.shippingCountry || 'CH',
          billingCountry: data.billingCountry || 'CH',
        };

        setFormData(formattedData);
      } catch (err) {
        setError(t('accountSettings.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [t]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle country select change
  const handleCountryChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle language change
  const handleLanguageChange = async (e) => {
    const { value } = e.target;
    try {
      setSaving(true);
      // Update language in backend
      await accountService.updateLanguage(value);

      // Update language in user profile and localStorage
      const userData = JSON.parse(localStorage.getItem('user')) || {};
      userData.language = value;
      localStorage.setItem('user', JSON.stringify(userData));

      // Update Redux store with new language
      dispatch(updateProfile({ language: value }));

      // Change UI language
      i18n.changeLanguage(value);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t('accountSettings.languageUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle profile save
  const handleProfileSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = await accountService.updateProfile(formData);
      dispatch(updateProfile(data));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t('accountSettings.profileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    // Reset any previous errors
    setError(null);

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('accountSettings.passwordMismatch'));
      return;
    }

    if (formData.newPassword.length < 8) {
      setError(t('accountSettings.passwordTooShort'));
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      setError(t('accountSettings.passwordRequirements'));
      return;
    }

    try {
      setSaving(true);
      await accountService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // Clear password fields
      setFormData((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    } catch (err) {
      console.error('Password change error:', err);
      // Display the actual error message from the API if available
      if (err.response?.status === 400 && err.response?.data?.message?.includes('incorrect')) {
        setError(t('accountSettings.currentPasswordIncorrect'));
      } else {
        setError(err.response?.data?.message || t('accountSettings.passwordChangeError'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle notification preferences save
  const handleNotificationSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await accountService.updateNotificationPreferences({
        emailNotifications: formData.emailNotifications,
        orderUpdates: formData.orderUpdates,
        promotionalEmails: formData.promotionalEmails,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t('accountSettings.notificationUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSuccess(false);
    setError(null);
  };

  return {
    loading,
    saving,
    error,
    success,
    formData,
    handleInputChange,
    handleCountryChange,
    handleLanguageChange,
    handleProfileSave,
    handlePasswordChange,
    handleNotificationSave,
    handleSnackbarClose,
  };
};

export default useAccountSettings;
